/**
 * <s-quick-card> — product card with colour swatches and hover size quick-add.
 * Reads [data-quick-card-data] JSON. A swatch click swaps the card photo, link
 * hover photo and size availability to that colour; a size click points the hidden
 * input[name="id"] at the matching variant before s-product-form submits it.
 */
class SQuickCard extends HTMLElement {
  #ac;
  #data = { variants: [], colors: [] };
  #color = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    try {
      this.#data = JSON.parse(this.querySelector('[data-quick-card-data]')?.textContent || '{}');
    } catch {
      return;
    }
    this.#data.variants ??= [];
    this.#data.colors ??= [];

    const pressed = this.querySelector('[data-color][aria-pressed="true"]');
    this.#color = pressed?.dataset.color ?? this.#data.colors[0]?.name ?? null;

    this.addEventListener('click', (event) => this.#onClick(event), opts);
    // After a swatch click keep the chosen colour photo visible: hide the hover photo until the pointer leaves the card.
    this.addEventListener('pointerleave', () => this.#hoverImage()?.style.removeProperty('opacity'), opts);
    this.#syncHover(this.#data.colors.find((entry) => entry.name === this.#color));
    this.#syncSizes();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onClick(event) {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('[data-color]');
    if (swatch && this.contains(swatch)) {
      event.preventDefault();
      this.#selectColor(swatch.dataset.color ?? '');
      return;
    }

    const sizeButton = event.target.closest('button[data-size]');
    if (sizeButton && this.contains(sizeButton)) {
      const variant = this.#variantFor(sizeButton.dataset.size ?? '');
      if (!variant || !variant.available) {
        event.preventDefault();
        return;
      }
      const input = sizeButton.form?.elements.namedItem('id');
      if (input instanceof HTMLInputElement) input.value = String(variant.id);
    }
  }

  #variantFor(size) {
    return this.#data.variants.find(
      (variant) => (!this.#color || variant.color === this.#color) && variant.size === size
    ) ?? (this.#color ? null : this.#data.variants.find((variant) => variant.available));
  }

  #hoverImage() {
    const hoverImage = this.querySelector('[data-card-media] img[data-card-hover]');
    return hoverImage instanceof HTMLImageElement ? hoverImage : null;
  }

  #syncHover(color) {
    const hoverImage = this.#hoverImage();
    if (!color || !hoverImage) return;
    if (color.hoverSrc) {
      if (color.hoverSrcset) hoverImage.srcset = color.hoverSrcset;
      hoverImage.src = color.hoverSrc;
      hoverImage.hidden = false;
    } else {
      hoverImage.hidden = true;
    }
  }

  #selectColor(name) {
    this.#color = name;

    for (const swatch of this.querySelectorAll('[data-color]')) {
      swatch.setAttribute('aria-pressed', String(swatch.dataset.color === name));
    }

    const color = this.#data.colors.find((entry) => entry.name === name);
    const image = this.querySelector('[data-card-media] img:not([data-card-hover])');
    if (color?.src && image instanceof HTMLImageElement) {
      image.loading = 'eager';
      if (color.srcset) image.srcset = color.srcset;
      image.src = color.src;
    }
    this.#syncHover(color);
    this.#hoverImage()?.style.setProperty('opacity', '0');
    if (color?.url) {
      for (const link of this.querySelectorAll('[data-card-media], article > a[href]')) {
        link.setAttribute('href', color.url);
      }
    }

    this.#syncSizes();
  }

  #syncSizes() {
    for (const button of this.querySelectorAll('button[data-size]')) {
      const variant = this.#variantFor(button.dataset.size ?? '');
      button.disabled = !variant || !variant.available;
    }
  }
}

if (!customElements.get('s-quick-card')) customElements.define('s-quick-card', SQuickCard);
